import { useState, useEffect } from 'react';
import { ScoreCategory, TeamName, useScore, Member } from '../../../context/ScoreContext';
import { useComments } from '../../../hooks/useComments';
import { useRatings } from '../../../hooks/useRatings';
import { useRatingItems } from '../../../hooks/useRatingItems';
import { useRatingArchives } from '../../../hooks/useRatingArchives';
import { useMemoryWall } from '../../../hooks/useMemoryWall';
import TaskManagementPanel from './TaskManagementPanel';
import MemberManagementPanel from './MemberManagementPanel';
import LotteryManagementPanel from './LotteryManagementPanel';
import RatingPanel from './RatingPanel';
import SecretAngelPanel from './SecretAngelPanel';
import { MISSION_TASK_KEY } from '../../../data/scheduleData';
import { useSoundEffect } from '../../../hooks/useSoundEffect';

const PANEL_PIN = '2266';
const PIN_STORAGE_KEY = 'srs_judge_pin_v1';
const getCurrentPin = () => localStorage.getItem(PIN_STORAGE_KEY) ?? PANEL_PIN;

const CATEGORIES: { key: ScoreCategory; label: string; icon: string; color: string }[] = [
  { key: 'battle',    label: '戰鬥積分', icon: 'ri-boxing-line',  color: '#e05555' },
  { key: 'chemistry', label: '默契加分', icon: 'ri-heart-2-line', color: '#d4a843' },
];
const QUICK_POINTS = [1, 2, 3, 5, 10];
const FONT_HUD  = "'Orbitron', monospace";
const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";

// ── Confirm Dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="absolute inset-0 bg-[#06060f]/92 flex items-center justify-center z-20 rounded-none backdrop-blur-sm">
      <div className="mx-4 w-full max-w-xs text-center p-6 rounded-2xl border border-[#e05555]/25"
        style={{ background: 'linear-gradient(160deg, #110c0c, #0a0808)' }}>
        <div className="w-14 h-14 rounded-full border border-[#e05555]/30 bg-[#e05555]/10 flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-fill text-[#e05555] text-2xl" />
        </div>
        <h3 className="text-white font-bold text-base mb-1.5">確定要重置？</h3>
        <p className="text-[#555] text-sm mb-5 leading-relaxed">所有積分將歸零，此動作無法復原。</p>
        <div className="flex gap-2.5">
          <button onClick={onCancel} className="flex-1 border border-[#2a2a3a] text-[#666] rounded-xl py-2.5 text-sm cursor-pointer hover:border-[#3a3a4a] hover:text-[#aaa] transition-all whitespace-nowrap">取消</button>
          <button onClick={onConfirm} className="flex-1 bg-[#e05555] text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer hover:bg-[#c94444] transition-all whitespace-nowrap">確認重置</button>
        </div>
      </div>
    </div>
  );
}

// ── PIN Lock ──────────────────────────────────────────────────────────────────
function PinLock({ onUnlock }: { onUnlock: () => void }) {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [error, setError] = useState(false);
  const { play } = useSoundEffect();

  const handleKey = (val: string) => {
    if (pin.length >= 4) return;
    play('click');
    const next = pin + val;
    setPin(next);
    setError(false);
    if (next.length === 4) {
      if (next === getCurrentPin()) {
        play('success');
        setTimeout(() => onUnlock(), 200);
      } else {
        setTimeout(() => {
          play('error');
          setShake(true); setError(true);
          setTimeout(() => { setShake(false); setPin(''); }, 600);
        }, 150);
      }
    }
  };
  const handleDel = () => { setPin(p => p.slice(0, -1)); setError(false); play('click'); };
  const dotColor = error ? '#e05555' : '#d4a843';

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-8 py-10">
      <div className="relative w-20 h-20 flex items-center justify-center rounded-full mb-6"
        style={{ background: 'radial-gradient(circle, #d4a84320, #d4a84306)', border: '1.5px solid #d4a84340', boxShadow: '0 0 32px rgba(212,168,67,0.18)' }}>
        <i className="ri-shield-keyhole-line text-4xl text-[#d4a843]" />
        <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: '1px solid #d4a84320', animation: 'pinRing 2.5s ease-in-out infinite' }} />
      </div>
      <h2 className="text-white text-xl font-black mb-1 tracking-wider" style={{ fontFamily: FONT_DECO }}>系統管理</h2>
      <p className="text-[#2a2a4a] text-[10px] tracking-[0.5em] uppercase mb-8" style={{ fontFamily: FONT_DECO }}>JUDGE ACCESS ONLY</p>
      <div className={`flex items-center gap-5 mb-5 ${shake ? 'animate-[shake_0.5s_ease]' : ''}`}>
        {[0,1,2,3].map(i => (
          <div key={i} className="relative w-5 h-5 flex items-center justify-center transition-all duration-150">
            <div className="w-4 h-4 rounded-full border-2 transition-all duration-200"
              style={{ background: i < pin.length ? dotColor : 'transparent', borderColor: i < pin.length ? dotColor : '#2a2a4a', transform: i < pin.length ? 'scale(1.2)' : 'scale(1)', boxShadow: i < pin.length ? `0 0 10px ${dotColor}70` : 'none' }} />
          </div>
        ))}
      </div>
      <p className={`text-xs mb-7 tracking-wider transition-colors ${error ? 'text-[#e05555]' : pin.length === 4 && !error ? 'text-[#7ec8a0]' : 'text-[#2a2a4a]'}`} style={{ fontFamily: FONT_BODY }}>
        {error ? '密碼錯誤，請再試一次' : pin.length === 4 ? '驗證中…' : '請輸入 4 位數密碼'}
      </p>
      <div className="grid grid-cols-3 gap-3 w-full max-w-[230px]">
        {['1','2','3','4','5','6','7','8','9','','0','del'].map((k, idx) => {
          if (k === '') return <div key={idx} />;
          const isDel = k === 'del';
          return (
            <button key={k} onClick={() => isDel ? handleDel() : handleKey(k)}
              className={`h-14 rounded-2xl font-bold cursor-pointer transition-all active:scale-90 ${isDel ? 'text-[#555] hover:text-[#888] bg-transparent text-lg' : 'text-white text-xl border'}`}
              style={isDel ? {} : { background: 'linear-gradient(160deg, #0e0f1e, #08090f)', borderColor: '#1e2035', boxShadow: '0 2px 8px rgba(0,0,0,0.4)', fontFamily: FONT_HUD }}
              onMouseEnter={!isDel ? e => { (e.currentTarget as HTMLElement).style.borderColor = '#d4a84340'; } : undefined}
              onMouseLeave={!isDel ? e => { (e.currentTarget as HTMLElement).style.borderColor = '#1e2035'; } : undefined}
            >
              {isDel ? <i className="ri-delete-back-2-line" /> : k}
            </button>
          );
        })}
      </div>
      <style>{`@keyframes pinRing { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.12); opacity: 0.15; } }`}</style>
    </div>
  );
}

// ── Member Picker ─────────────────────────────────────────────────────────────
function MemberPicker({ members, selected, onSelect }: { members: Member[]; selected: Member | null; onSelect: (m: Member) => void }) {
  const blackMembers = members.filter(m => m.team === 'black');
  const whiteMembers = members.filter(m => m.team === 'white');
  const renderGroup = (list: Member[], teamName: 'black' | 'white') => {
    if (list.length === 0) return null;
    const color = teamName === 'black' ? '#d4a843' : '#e0e0e0';
    const label = teamName === 'black' ? '黑隊' : '白隊';
    return (
      <div key={teamName} className="mb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] tracking-widest uppercase font-bold" style={{ color, fontFamily: FONT_DECO }}>{label}</span>
          <div className="h-px flex-1" style={{ background: `${color}18` }} />
        </div>
        <div className="flex flex-col gap-1">
          {list.map(m => {
            const isSelected = selected?.id === m.id;
            return (
              <button key={m.id} onClick={() => onSelect(m)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left cursor-pointer transition-all"
                style={{ background: isSelected ? `${color}10` : '#0d0e1a', border: `1px solid ${isSelected ? color + '40' : '#16172a'}` }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                  style={{ background: `${color}18`, color, border: `1px solid ${color}25`, fontFamily: FONT_BODY }}>
                  {m.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium flex-1" style={{ color: isSelected ? 'white' : '#555', fontFamily: FONT_BODY }}>{m.name}</span>
                {isSelected && <i className="ri-check-line shrink-0 text-sm" style={{ color }} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  if (members.length === 0) return (
    <div className="text-center py-6 text-[#2a2a4a]">
      <i className="ri-group-line text-2xl mb-1 block" />
      <p className="text-xs" style={{ fontFamily: FONT_BODY }}>尚無成員</p>
    </div>
  );
  return (
    <div className="max-h-48 overflow-y-auto pr-1 space-y-1">
      {renderGroup(blackMembers, 'black')}
      {renderGroup(whiteMembers, 'white')}
    </div>
  );
}

// ── Score Form Controls ───────────────────────────────────────────────────────
function ScoreControls({ category, setCategory, points, setPoints, isDeduct, setIsDeduct, accentColor }: {
  category: ScoreCategory; setCategory: (c: ScoreCategory) => void;
  points: number; setPoints: (p: number) => void;
  isDeduct: boolean; setIsDeduct: (v: boolean) => void;
  accentColor?: string;
}) {
  const color = accentColor ?? '#d4a843';
  return (
    <>
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>積分類型</p>
        <div className="grid grid-cols-2 gap-1.5">
          {CATEGORIES.map(c => {
            const active = category === c.key;
            return (
              <button key={c.key} onClick={() => setCategory(c.key)}
                className="flex items-center gap-2 px-2.5 py-2 rounded-xl cursor-pointer transition-all border text-left"
                style={{ background: active ? `${c.color}10` : '#0d0e1a', borderColor: active ? `${c.color}40` : '#16172a' }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: active ? `${c.color}18` : '#111422', border: `1px solid ${active ? c.color+'30' : '#1e2035'}` }}>
                  <i className={`${c.icon} text-sm`} style={{ color: active ? c.color : '#2a2a4a' }} />
                </div>
                <span className="text-xs font-bold" style={{ color: active ? 'white' : '#444', fontFamily: FONT_BODY }}>{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>操作類型</p>
        <div className="flex p-1 rounded-xl gap-1" style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
          <button onClick={() => setIsDeduct(false)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
            style={!isDeduct ? { background: '#7ec8a018', color: '#7ec8a0', border: '1px solid #7ec8a030' } : { color: '#2a3a4a', border: '1px solid transparent' }}>
            <i className="ri-add-line" />加分
          </button>
          <button onClick={() => setIsDeduct(true)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
            style={isDeduct ? { background: '#e0555518', color: '#e05555', border: '1px solid #e0555530' } : { color: '#2a3a4a', border: '1px solid transparent' }}>
            <i className="ri-subtract-line" />扣分
          </button>
        </div>
      </div>
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>選擇分數</p>
        <div className="grid grid-cols-5 gap-1 mb-2">
          {QUICK_POINTS.map(p => {
            const active = points === p;
            const activeColor = isDeduct ? '#e05555' : color;
            return (
              <button key={p} onClick={() => setPoints(p)}
                className="py-2 rounded-xl text-sm font-black cursor-pointer transition-all whitespace-nowrap"
                style={{ background: active ? activeColor : '#0d0e1a', color: active ? (isDeduct ? 'white' : '#06060f') : '#3a4a6a', border: `1px solid ${active ? 'transparent' : '#16172a'}`, boxShadow: active ? `0 0 10px ${activeColor}40` : 'none', fontFamily: FONT_HUD }}>
                {p}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
          <i className="ri-calculator-line text-[#2a3a5a] text-sm shrink-0" />
          <input type="number" min={1} value={points} onChange={e => setPoints(Math.max(1, parseInt(e.target.value) || 1))}
            className="bg-transparent text-white text-sm w-full outline-none" style={{ fontFamily: FONT_HUD }} placeholder="自訂分數" />
          <span className="text-[#2a3a5a] text-xs shrink-0" style={{ fontFamily: FONT_BODY }}>分</span>
        </div>
      </div>
    </>
  );
}

// ── Team Score Tab ────────────────────────────────────────────────────────────
function TeamScoreTab({ onFeedback }: { onFeedback: (msg: string) => void }) {
  const { addScore } = useScore();
  const [team, setTeam] = useState<TeamName>('black');
  const [category, setCategory] = useState<ScoreCategory>('battle');
  const [points, setPoints] = useState<number>(1);
  const [note, setNote] = useState('');
  const [isDeduct, setIsDeduct] = useState(false);
  const catMeta = CATEGORIES.find(c => c.key === category)!;
  const accentColor = team === 'black' ? '#d4a843' : '#e0e0e0';
  const { play } = useSoundEffect();

  const handleSubmit = () => {
    const finalPoints = isDeduct ? -Math.abs(points) : Math.abs(points);
    if (!finalPoints) return;
    addScore(team, category, finalPoints, note || catMeta.label);
    onFeedback(`✓ ${team === 'black' ? '黑隊' : '白隊'} ${finalPoints > 0 ? '+' : ''}${finalPoints} 分`);
    setNote('');
    play('confirm');
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>選擇隊伍</p>
        <div className="grid grid-cols-2 gap-2">
          {(['black','white'] as TeamName[]).map(t => {
            const tc = t === 'black' ? '#d4a843' : '#e8e8e8';
            const active = team === t;
            return (
              <button key={t} onClick={() => { setTeam(t); play('click'); }}
                className="relative py-2.5 rounded-xl text-sm font-black cursor-pointer transition-all overflow-hidden"
                style={{ background: active ? (t==='black' ? 'linear-gradient(135deg,#d4a843,#c0902a)' : 'linear-gradient(135deg,#e8e8e8,#c8c8c8)') : '#0d0e1a', color: active ? '#06060f' : tc+'50', border: `1.5px solid ${active ? 'transparent' : tc+'18'}`, boxShadow: active ? `0 0 18px ${tc}35` : 'none', fontFamily: FONT_BODY }}>
                {active && <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'linear-gradient(135deg,rgba(255,255,255,0.5),transparent 50%)' }} />}
                <div className="relative flex items-center justify-center gap-2">
                  <i className={`${t==='black' ? 'ri-shield-fill' : 'ri-sword-fill'} text-base`} />
                  <span className="tracking-wider">{t==='black' ? '黑隊' : '白隊'}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <ScoreControls category={category} setCategory={setCategory} points={points} setPoints={setPoints} isDeduct={isDeduct} setIsDeduct={setIsDeduct} accentColor={accentColor} />
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>備注（可選）</p>
        <div className="flex items-center gap-2.5 rounded-xl px-3 py-2" style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
          <i className="ri-edit-line text-[#2a3a5a] text-sm shrink-0" />
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="例：老街尋寶第一名"
            className="bg-transparent text-white text-sm w-full outline-none placeholder:text-[#1e2035]" style={{ fontFamily: FONT_BODY }} />
        </div>
      </div>
      <button onClick={handleSubmit}
        className="w-full py-3 rounded-xl font-bold text-sm cursor-pointer transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2 relative overflow-hidden"
        style={{ background: isDeduct ? 'linear-gradient(135deg,#e05555,#c44040)' : (team==='black' ? 'linear-gradient(135deg,#d4a843,#c0902a)' : 'linear-gradient(135deg,#e8e8e8,#c8c8c8)'), color: '#06060f', boxShadow: `0 0 18px ${isDeduct ? 'rgba(224,85,85,0.3)' : team==='black' ? 'rgba(212,168,67,0.3)' : 'rgba(232,232,232,0.15)'}`, fontFamily: FONT_BODY }}>
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)', animation: 'shimBtn 3s linear infinite' }} />
        <i className={`${isDeduct ? 'ri-subtract-circle-fill' : 'ri-add-circle-fill'} text-base`} />
        <span className="font-black tracking-wide">{isDeduct ? '扣除' : '加入'} {team==='black' ? '黑隊' : '白隊'} · {isDeduct ? '-' : '+'}{points} 分</span>
      </button>
    </div>
  );
}

// ── Individual Score Tab ──────────────────────────────────────────────────────
function IndividualScoreTab({ onFeedback }: { onFeedback: (msg: string) => void }) {
  const { addScore, members } = useScore();
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [category, setCategory] = useState<ScoreCategory>('battle');
  const [points, setPoints] = useState<number>(1);
  const [reason, setReason] = useState('');
  const [isDeduct, setIsDeduct] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const catMeta = CATEGORIES.find(c => c.key === category)!;
  const accentColor = selectedMember?.team==='black' ? '#d4a843' : selectedMember?.team==='white' ? '#e0e0e0' : '#d4a843';
  const { play } = useSoundEffect();

  const handleSubmit = () => {
    if (!selectedMember) return;
    if (!reason.trim()) { setReasonError('請填寫給分原因'); play('error'); return; }
    const finalPoints = isDeduct ? -Math.abs(points) : Math.abs(points);
    addScore(selectedMember.team, category, finalPoints, reason.trim(), selectedMember.id, selectedMember.name);
    onFeedback(`✓ ${selectedMember.name} ${finalPoints > 0 ? '+' : ''}${finalPoints} 分`);
    setReason(''); setReasonError('');
    play('confirm');
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>選擇成員</p>
        <MemberPicker members={members} selected={selectedMember} onSelect={(m) => { setSelectedMember(m); play('click'); }} />
      </div>
      {selectedMember && (
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border relative overflow-hidden"
          style={{ borderColor: `${accentColor}30`, background: `${accentColor}06` }}>
          <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl" style={{ background: `linear-gradient(180deg,${accentColor},${accentColor}40)` }} />
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black shrink-0"
            style={{ background: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30`, fontFamily: FONT_BODY }}>
            {selectedMember.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-bold" style={{ fontFamily: FONT_BODY }}>{selectedMember.name}</p>
            <p className="text-[10px] tracking-widest" style={{ color: `${accentColor}60`, fontFamily: FONT_DECO }}>{selectedMember.team==='black' ? 'BLACK' : 'WHITE'} TEAM</p>
          </div>
          <button onClick={() => { setSelectedMember(null); play('click'); }} className="text-[#2a3a5a] hover:text-[#666] cursor-pointer transition-colors"><i className="ri-close-line text-base" /></button>
        </div>
      )}
      {selectedMember && (
        <>
          <ScoreControls category={category} setCategory={setCategory} points={points} setPoints={setPoints} isDeduct={isDeduct} setIsDeduct={setIsDeduct} accentColor={accentColor} />
          <div>
            <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-1.5" style={{ fontFamily: FONT_DECO }}>給分原因 <span className="text-[#e05555]">*</span></p>
            <div className="flex items-center gap-2.5 rounded-xl px-3 py-2" style={{ background: '#0a0b16', border: `1px solid ${reasonError ? '#e05555' : '#16172a'}` }}>
              <i className="ri-chat-1-line text-[#2a3a5a] text-sm shrink-0" />
              <input type="text" value={reason} onChange={e => { setReason(e.target.value); setReasonError(''); }} placeholder="例：主動幫助隊友、表現突出..." maxLength={60}
                className="bg-transparent text-white text-sm w-full outline-none placeholder:text-[#1e2035]" style={{ fontFamily: FONT_BODY }} />
            </div>
            {reasonError && <p className="text-[#e05555] text-xs mt-1 flex items-center gap-1" style={{ fontFamily: FONT_BODY }}><i className="ri-error-warning-line" />{reasonError}</p>}
          </div>
          <button onClick={handleSubmit}
            className="w-full py-3 rounded-xl font-bold text-sm cursor-pointer transition-all duration-200 whitespace-nowrap flex items-center justify-center gap-2 relative overflow-hidden"
            style={{ background: isDeduct ? 'linear-gradient(135deg,#e05555,#c44040)' : `linear-gradient(135deg,${accentColor},${accentColor}bb)`, color: '#06060f', boxShadow: `0 0 16px ${isDeduct ? 'rgba(224,85,85,0.25)' : 'rgba(212,168,67,0.25)'}`, fontFamily: FONT_BODY }}>
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)', animation: 'shimBtn 3s linear infinite' }} />
            <i className={`${isDeduct ? 'ri-subtract-circle-fill' : 'ri-add-circle-fill'} text-base`} />
            <span className="font-black tracking-wide relative z-10">{isDeduct ? '扣除' : '給予'} {selectedMember.name} {isDeduct ? '-' : '+'}{points} {catMeta.label}</span>
          </button>
        </>
      )}
    </div>
  );
}

// ── Settlement Button ─────────────────────────────────────────────────────────
function SettlementButton({ onFeedback }: { onFeedback: (msg: string) => void }) {
  const { setSettlementActive, scheduleState, scores } = useScore();
  const { settlementActive } = scheduleState;
  const [confirm, setConfirm] = useState(false);
  const { play } = useSoundEffect();

  const blackTotal = scores.black.battle + scores.black.chemistry + scores.black.dreamCoins;
  const whiteTotal = scores.white.battle + scores.white.chemistry + scores.white.dreamCoins;

  const handleActivate = () => {
    setSettlementActive(true);
    setConfirm(false);
    onFeedback('✓ 結算畫面已同步至所有裝置');
    play('success');
  };

  const handleDeactivate = () => {
    setSettlementActive(false);
    onFeedback('✓ 結算畫面已關閉');
    play('stop');
  };

  if (settlementActive) {
    return (
      <div className="rounded-xl border overflow-hidden" style={{ background: 'linear-gradient(160deg,#04120a,#030d07)', borderColor: '#7ec8a040' }}>
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7ec8a0]" />
            </span>
            <p className="text-[#7ec8a0] text-xs font-bold whitespace-nowrap" style={{ fontFamily: FONT_BODY }}>結算畫面播出中</p>
          </div>
          <button onClick={handleDeactivate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
            style={{ background: '#e0555518', color: '#e05555', border: '1px solid #e0555530', fontFamily: FONT_BODY }}>
            <i className="ri-stop-circle-line text-xs" />關閉
          </button>
        </div>
      </div>
    );
  }

  if (confirm) {
    return (
      <div className="rounded-xl border overflow-hidden" style={{ background: 'linear-gradient(160deg,#12080a,#0a0505)', borderColor: '#d4a84340' }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: '#d4a84315' }}>
          <p className="text-white text-xs font-black mb-0.5" style={{ fontFamily: FONT_BODY }}>確認推播結算畫面？</p>
          <p className="text-[#3a4a2a] text-[10px] leading-relaxed" style={{ fontFamily: FONT_BODY }}>所有裝置將同步出現計分結果及冠軍玩家</p>
        </div>
        <div className="px-3 py-2.5 grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1 px-3 py-2 rounded-xl" style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
            <span className="text-[9px] tracking-widest uppercase" style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>黑隊</span>
            <span className="font-black tabular-nums text-lg leading-none" style={{ color: '#d4a843', fontFamily: FONT_HUD }}>{blackTotal}</span>
          </div>
          <div className="flex flex-col gap-1 px-3 py-2 rounded-xl" style={{ background: '#0a0b16', border: '1px solid #16172a' }}>
            <span className="text-[9px] tracking-widest uppercase" style={{ color: '#2a3a5a', fontFamily: FONT_DECO }}>白隊</span>
            <span className="font-black tabular-nums text-lg leading-none" style={{ color: '#e0e0e0', fontFamily: FONT_HUD }}>{whiteTotal}</span>
          </div>
        </div>
        <div className="px-3 pb-3 flex gap-2">
          <button onClick={() => { setConfirm(false); play('click'); }}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
            style={{ border: '1px solid #2a2a3a', color: '#555', fontFamily: FONT_BODY }}>取消</button>
          <button onClick={handleActivate}
            className="flex-1 py-2.5 rounded-xl text-xs font-black cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap flex items-center justify-center gap-1.5"
            style={{ background: 'linear-gradient(135deg,#d4a843,#c0902a)', color: '#06060f', fontFamily: FONT_BODY }}>
            <i className="ri-trophy-line text-sm" />立即推播
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => { setConfirm(true); play('click'); }}
      className="w-full flex items-center gap-3 py-3.5 px-4 rounded-xl cursor-pointer transition-all active:scale-[0.97] relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg,#d4a84318,#d4a84308)', border: '1.5px solid rgba(212,168,67,0.40)', boxShadow: '0 0 20px rgba(212,168,67,0.12)' }}>
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)', animation: 'shimBtn 3s linear infinite' }} />
      <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0 relative z-10"
        style={{ background: 'linear-gradient(135deg,#d4a843,#c0902a)', boxShadow: '0 0 16px rgba(212,168,67,0.4)' }}>
        <i className="ri-trophy-2-fill text-[#06060f] text-base" />
      </div>
      <div className="text-left flex-1 min-w-0 relative z-10">
        <p className="text-[#d4a843] text-sm font-black tracking-wide" style={{ fontFamily: FONT_BODY }}>結算成績</p>
        <p className="text-[#3a3a1a] text-[10px]" style={{ fontFamily: FONT_BODY }}>同步推播計分結果 · 揭曉冠軍</p>
      </div>
      <i className="ri-arrow-right-line text-[#d4a84340] text-base shrink-0" />
    </button>
  );
}

// ── Change PIN Section ────────────────────────────────────────────────────────
function ChangePinSection({ onFeedback }: { onFeedback: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'new'|'confirm'>('new');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [shake, setShake] = useState(false);
  const [error, setError] = useState('');
  const currentPin = getCurrentPin();
  const reset = () => { setStep('new'); setNewPin(''); setConfirmPin(''); setError(''); setShake(false); };
  const handleClose = () => { setOpen(false); reset(); };

  const handleKey = (val: string) => {
    if (step === 'new') {
      if (newPin.length >= 4) return;
      const next = newPin + val; setNewPin(next); setError('');
      if (next.length === 4) setTimeout(() => setStep('confirm'), 250);
    } else {
      if (confirmPin.length >= 4) return;
      const next = confirmPin + val; setConfirmPin(next); setError('');
      if (next.length === 4) {
        if (next === newPin) { localStorage.setItem(PIN_STORAGE_KEY, next); onFeedback('✓ 密碼已更新'); handleClose(); }
        else setTimeout(() => { setShake(true); setError('兩次輸入不一致，請重新設定'); setTimeout(() => { setShake(false); reset(); }, 700); }, 150);
      }
    }
  };
  const handleDel = () => { setError(''); if (step==='new') setNewPin(p=>p.slice(0,-1)); else setConfirmPin(p=>p.slice(0,-1)); };
  const dotColor = error ? '#e05555' : '#d4a843';
  const activePin = step==='new' ? newPin : confirmPin;

  return (
    <div className="rounded-2xl overflow-hidden border border-[#1e1030]" style={{ background: 'linear-gradient(160deg,#0d0a18,#090810)' }}>
      <button type="button" onClick={() => open ? handleClose() : setOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer transition-all active:opacity-70"
        style={{ background: open ? 'rgba(212,168,67,0.06)' : 'transparent', touchAction: 'manipulation', minHeight: '52px' }}>
        <div className="w-8 h-8 flex items-center justify-center rounded-xl shrink-0" style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.3)' }}>
          <i className="ri-lock-password-line text-sm" style={{ color: '#d4a843' }} />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-white text-xs font-bold" style={{ fontFamily: FONT_BODY }}>修改裁判密碼</p>
          <p className="text-[#2a3a5a] text-[10px]" style={{ fontFamily: FONT_BODY }}>目前：{currentPin.split('').map(()=>'●').join(' ')}</p>
        </div>
        <i className={`ri-arrow-${open?'up':'down'}-s-line text-sm`} style={{ color: '#2a3a5a' }} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'rgba(212,168,67,0.1)' }}>
          <div className="flex items-center gap-2 pt-3 pb-4">
            {(['new','confirm'] as const).map((s,i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0"
                  style={{ background: step===s ? 'rgba(212,168,67,0.2)' : s==='new'&&step==='confirm' ? 'rgba(126,200,160,0.2)' : 'rgba(42,42,74,0.3)', border: `1px solid ${step===s ? 'rgba(212,168,67,0.4)' : s==='new'&&step==='confirm' ? 'rgba(126,200,160,0.4)' : 'rgba(42,42,74,0.3)'}`, color: step===s ? '#d4a843' : s==='new'&&step==='confirm' ? '#7ec8a0' : '#2a3a5a', fontFamily: FONT_HUD }}>
                  {s==='new'&&step==='confirm' ? <i className="ri-check-line text-[9px]" /> : i+1}
                </div>
                <span className="text-[10px] font-bold" style={{ color: step===s ? '#d4a843' : s==='new'&&step==='confirm' ? '#7ec8a0' : '#2a3a5a', fontFamily: FONT_BODY }}>{s==='new' ? '輸入新密碼' : '再次確認'}</span>
                {i===0 && <div className="h-px flex-1" style={{ background: step==='confirm' ? 'rgba(126,200,160,0.3)' : 'rgba(42,42,74,0.3)' }} />}
              </div>
            ))}
          </div>
          <div className={`flex items-center justify-center gap-4 mb-3 ${shake ? 'animate-[shake_0.5s_ease]' : ''}`}>
            {[0,1,2,3].map(i => (
              <div key={i} className="w-4 h-4 rounded-full border-2 transition-all duration-200"
                style={{ background: i<activePin.length ? dotColor : 'transparent', borderColor: i<activePin.length ? dotColor : '#2a2a4a', transform: i<activePin.length ? 'scale(1.2)' : 'scale(1)', boxShadow: i<activePin.length ? `0 0 8px ${dotColor}70` : 'none' }} />
            ))}
          </div>
          {error && <p className="text-center text-[10px] mb-2 text-[#e05555]" style={{ fontFamily: FONT_BODY }}>{error}</p>}
          {!error && <p className="text-center text-[10px] mb-2" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>{step==='new' ? '請輸入 4 位數新密碼' : '再輸入一次確認'}</p>}
          <div className="grid grid-cols-3 gap-2">
            {['1','2','3','4','5','6','7','8','9','','0','del'].map((k,idx) => {
              if (k==='') return <div key={idx} />;
              const isDel = k==='del';
              return (
                <button key={k} type="button" onClick={() => isDel ? handleDel() : handleKey(k)}
                  className={`h-11 rounded-xl font-bold cursor-pointer transition-all active:scale-90 ${isDel ? 'text-[#555] hover:text-[#888] bg-transparent text-base' : 'text-white text-lg border'}`}
                  style={isDel ? {} : { background: 'linear-gradient(160deg,#0e0f1e,#08090f)', borderColor: '#1e2035', fontFamily: "'Orbitron', monospace" }}>
                  {isDel ? <i className="ri-delete-back-2-line" /> : k}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={handleClose} className="w-full mt-3 py-2 rounded-xl text-xs cursor-pointer whitespace-nowrap" style={{ color: '#2a3a5a', border: '1px solid #16172a', fontFamily: FONT_BODY }}>取消</button>
        </div>
      )}
    </div>
  );
}

// ── Reset Tab ─────────────────────────────────────────────────────────────
function ResetTab({ onFeedback }: { onFeedback: (msg: string) => void }) {
  const {
    resetGame, resetMembers, resetMottos, resetEventStatuses,
    resetTaskUnlocks, resetLotteryItems, resetScores, resetTaskCustomizations, resetAllTasks,
    setSecretPairings, toggleTaskUnlock, members, scores, scheduleState,
    setHiddenMissionActive, setHiddenMissionStatus,
  } = useScore();
  const { comments, clearAllComments } = useComments();
  const { clearAllRatings } = useRatings();
  const { clearAllItems: clearAllRatingItems } = useRatingItems();
  const { clearAllArchives: clearAllRatingArchives } = useRatingArchives();
  const { clearAllPhotos } = useMemoryWall();
  const [confirmed, setConfirmed] = useState(false);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState<'idle'|'done'>('idle');
  const [confirmingItem, setConfirmingItem] = useState<string|null>(null);
  const [itemRunning, setItemRunning] = useState<string|null>(null);
  const [doneItems, setDoneItems] = useState<Set<string>>(new Set());

  const totalScore = (scores.black.battle+scores.black.chemistry+scores.black.dreamCoins)+(scores.white.battle+scores.white.chemistry+scores.white.dreamCoins);

  const handleReset = async () => {
    if (!confirmed) return;
    setRunning(true);
    await resetGame();
    await clearAllComments();
    await clearAllRatings();
    await clearAllRatingItems();
    await clearAllRatingArchives();
    await clearAllPhotos();
    setRunning(false); setConfirmed(false); setStep('done');
    onFeedback('✓ 遊戲已完整重置');
    setTimeout(() => setStep('idle'), 3000);
  };

  const markDone = (id: string) => {
    setDoneItems(prev => new Set([...prev, id]));
    setTimeout(() => setDoneItems(prev => { const n = new Set(prev); n.delete(id); return n; }), 2500);
  };

  type ResetItemDef = { id: string; icon: string; label: string; color: string; handler: () => Promise<void> };
  const RESET_ITEMS: ResetItemDef[] = [
    { id:'members',        icon:'ri-group-line',       label:'所有成員資料與隊伍',                                     color:'#d4a843', handler: resetMembers },
    { id:'scores',         icon:'ri-trophy-line',      label:'黑白兩隊積分與紀錄',                                     color:'#7ec8a0', handler: async () => resetScores() },
    { id:'comments',       icon:'ri-chat-3-line',      label:'所有即時留言',                                           color:'#a78bfa', handler: clearAllComments },
    { id:'photos',         icon:'ri-image-2-line',     label:'回憶牆所有照片',                                         color:'#f0a86e', handler: async () => { await clearAllPhotos(); } },
    { id:'mottos',         icon:'ri-flag-2-line',      label:'兩隊戰隊口號',                                           color:'#e0e0e0', handler: resetMottos },
    { id:'allTasks',       icon:'ri-map-2-line',       label:'冒險旅程全部重置',                                       color:'#f0a86e', handler: resetAllTasks },
    { id:'secretPairings', icon:'ri-spy-line',         label:'小主人任務配對設置',                                     color:'#c06b9a', handler: async () => setSecretPairings({}) },
    { id:'revealEffect',   icon:'ri-heart-2-line',     label:'小主人揭曉特效狀態',                                     color:'#c06b9a', handler: async () => { if (scheduleState.unlockedTasks[MISSION_TASK_KEY]) toggleTaskUnlock(MISSION_TASK_KEY); } },
    { id:'hiddenMission',  icon:'ri-eye-close-line',   label:'隱藏任務（微光記憶：民宿密令）',                          color:'#c06b9a', handler: async () => { setHiddenMissionActive(false); setHiddenMissionStatus('idle'); } },
    { id:'lottery',        icon:'ri-lottery-line',     label:'命運抽籤籤池',                                           color:'#f0a86e', handler: resetLotteryItems },
    { id:'ratingItems',    icon:'ri-star-line',        label:'評分項目列表',                                           color:'#d4a843', handler: clearAllRatingItems },
    { id:'ratings',        icon:'ri-bar-chart-grouped-line', label:'當前投票評分紀錄',                                  color:'#7ec8a0', handler: clearAllRatings },
    { id:'ratingArchives', icon:'ri-archive-line',     label:'評分歷史封存紀錄',                                       color:'#a78bfa', handler: clearAllRatingArchives },
    { id:'rewards',        icon:'ri-gift-line',        label:'任務獎勵領取紀錄',                                       color:'#7ec8a0', handler: async () => { localStorage.removeItem('srs-claimed-rewards-v1'); localStorage.removeItem('srs-reward-history-v2'); } },
    { id:'story',          icon:'ri-history-line',     label:'開場故事（下次重播）',                                   color:'#888',    handler: async () => sessionStorage.removeItem('srs_story_v1') },
  ];

  const handleItemDelete = async (item: ResetItemDef) => {
    setItemRunning(item.id);
    try { await item.handler(); markDone(item.id); onFeedback(`✓ 已清除：${item.label}`); }
    finally { setItemRunning(null); setConfirmingItem(null); }
  };

  if (step === 'done') return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 gap-4">
      <div className="w-16 h-16 flex items-center justify-center rounded-full" style={{ background: '#7ec8a010', border: '1px solid #7ec8a030' }}>
        <i className="ri-check-line text-3xl text-[#7ec8a0]" />
      </div>
      <div className="text-center">
        <p className="text-white font-bold text-base mb-1" style={{ fontFamily: FONT_BODY }}>重置完成</p>
        <p className="text-[#3a4a6a] text-sm" style={{ fontFamily: FONT_BODY }}>所有資料已清空，可以開始新的一場</p>
      </div>
    </div>
  );

  return (
    <div className="flex-1 px-4 py-4 space-y-4">
      <ChangePinSection onFeedback={onFeedback} />

      {/* Warning notice */}
      <div className="rounded-xl border border-[#e05555]/20 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(224,85,85,0.04)' }}>
        <i className="ri-error-warning-fill text-[#e05555] text-lg shrink-0" />
        <div>
          <p className="text-[#e05555] font-bold text-xs mb-0.5" style={{ fontFamily: FONT_BODY }}>危險操作 — 無法復原</p>
          <p className="text-[#3a4a6a] text-[11px] leading-relaxed" style={{ fontFamily: FONT_BODY }}>可個別清除，或一次全部重置。所有裝置立即同步清空。</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label:'成員', value:members.length, icon:'ri-group-line', color:'#d4a843' },
          { label:'總積分', value:totalScore, icon:'ri-trophy-line', color:'#7ec8a0' },
          { label:'留言', value:comments.length, icon:'ri-chat-3-line', color:'#a78bfa' },
        ].map(item => (
          <div key={item.label} className="rounded-xl p-3 text-center border" style={{ background: '#0a0b16', borderColor: '#12131e' }}>
            <i className={`${item.icon} text-base mb-1 block`} style={{ color: item.color }} />
            <div className="text-white text-lg font-black tabular-nums" style={{ fontFamily: FONT_HUD }}>{item.value}</div>
            <div className="text-[#2a3a5a] text-[10px] mt-0.5 tracking-widest" style={{ fontFamily: FONT_BODY }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Individual reset items */}
      <div>
        <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-2" style={{ fontFamily: FONT_DECO }}>個別清除</p>
        <div className="space-y-1.5">
          {RESET_ITEMS.map(item => {
            const isConfirming = confirmingItem === item.id;
            const isRunning = itemRunning === item.id;
            const isDone = doneItems.has(item.id);
            if (isConfirming) return (
              <div key={item.id} className="flex items-center gap-2 py-2.5 px-3 rounded-xl border border-[#e05555]/20" style={{ background: 'rgba(224,85,85,0.05)' }}>
                <div className="w-7 h-7 flex items-center justify-center rounded-md shrink-0" style={{ background: `${item.color}15` }}>
                  <i className={`${item.icon} text-xs`} style={{ color: item.color }} />
                </div>
                <span className="text-[#888] text-xs flex-1 min-w-0 truncate" style={{ fontFamily: FONT_BODY }}>{item.label}</span>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setConfirmingItem(null)}
                    className="px-3 py-1.5 rounded-lg text-xs text-[#666] border border-[#1e2035] cursor-pointer active:opacity-70 whitespace-nowrap"
                    style={{ minHeight: '32px' }}>取消</button>
                  <button onClick={() => handleItemDelete(item)} disabled={isRunning}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap flex items-center gap-1"
                    style={{ background: 'rgba(224,85,85,0.15)', color: '#e05555', border: '1px solid rgba(224,85,85,0.3)', minHeight: '32px' }}>
                    {isRunning ? <i className="ri-loader-4-line animate-spin" /> : '確認清除'}
                  </button>
                </div>
              </div>
            );
            return (
              <div key={item.id} className="flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all"
                style={{ background: isDone ? 'rgba(126,200,160,0.05)' : '#080916', border: `1px solid ${isDone ? '#7ec8a020' : '#12131e'}` }}>
                <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: isDone ? '#7ec8a012' : `${item.color}10` }}>
                  <i className={`${item.icon} text-sm`} style={{ color: isDone ? '#7ec8a0' : item.color }} />
                </div>
                <span className="text-xs flex-1 min-w-0 truncate" style={{ color: isDone ? '#7ec8a0' : '#666', fontFamily: FONT_BODY }}>{item.label}</span>
                {isDone
                  ? <i className="ri-check-line text-[#7ec8a0] text-base shrink-0" />
                  : <button onClick={() => setConfirmingItem(item.id)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all shrink-0 active:scale-90"
                      style={{ color: '#2a2a3a', background: '#0d0e1a', border: '1px solid #16172a' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='#e05555'; (e.currentTarget as HTMLElement).style.borderColor='rgba(224,85,85,0.35)'; (e.currentTarget as HTMLElement).style.background='rgba(224,85,85,0.08)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='#2a2a3a'; (e.currentTarget as HTMLElement).style.borderColor='#16172a'; (e.currentTarget as HTMLElement).style.background='#0d0e1a'; }}>
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                }
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 一鍵全部重置 ── */}
      <div className="pt-2 border-t border-[#12131e]">
        {!confirmed ? (
          <button
            type="button"
            onClick={() => setConfirmed(true)}
            className="w-full flex items-center gap-3 py-4 px-4 rounded-2xl font-bold cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg,#e0555514,#e0555506)', border: '1.5px solid rgba(224,85,85,0.4)', color: '#e05555', fontFamily: FONT_BODY }}
          >
            <div className="absolute inset-0 pointer-events-none opacity-10"
              style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)', animation: 'shimBtn 3s linear infinite' }} />
            <div className="w-10 h-10 flex items-center justify-center rounded-full shrink-0 relative z-10"
              style={{ background: 'rgba(224,85,85,0.15)', border: '1px solid rgba(224,85,85,0.4)' }}>
              <i className="ri-restart-line text-lg" style={{ color: '#e05555' }} />
            </div>
            <div className="text-left flex-1 min-w-0 relative z-10">
              <p className="font-black text-sm tracking-wide">一鍵全部重置</p>
              <p className="text-[11px] font-normal opacity-60 mt-0.5">清除所有資料，重新開始一場</p>
            </div>
            <i className="ri-arrow-right-line text-base opacity-40 relative z-10" />
          </button>
        ) : (
          <div className="rounded-2xl border overflow-hidden" style={{ background: 'linear-gradient(160deg,#120808,#0a0505)', borderColor: 'rgba(224,85,85,0.45)' }}>
            <div className="px-4 py-3 border-b border-[#e05555]/20">
              <div className="flex items-center gap-2 mb-1.5">
                <i className="ri-error-warning-fill text-[#e05555] text-base" />
                <p className="text-[#e05555] text-sm font-black" style={{ fontFamily: FONT_BODY }}>確認全部重置？</p>
              </div>
              <p className="text-[#6a3030] text-xs leading-relaxed" style={{ fontFamily: FONT_BODY }}>
                成員、積分、留言、任務、配對、籤池等所有資料將被清除。此動作無法復原。
              </p>
            </div>
            <div className="px-3 py-3 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmed(false)}
                className="flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap"
                style={{ border: '1px solid #2a2a3a', color: '#666', fontFamily: FONT_BODY }}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={running}
                className="flex-1 py-3 rounded-xl text-sm font-black cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#e05555,#c94444)', color: '#fff', fontFamily: FONT_BODY }}
              >
                {running
                  ? <><i className="ri-loader-4-line animate-spin text-sm" /><span>重置中…</span></>
                  : <><i className="ri-restart-line text-sm" /><span>確認全部重置</span></>
                }
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Game Start Banner ─────────────────────────────────────────────────────────
function GameStartBanner({ gameStartedAt, onStart }: { gameStartedAt: number | null; onStart: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [starting, setStarting] = useState(false);
  const [blink, setBlink] = useState(true);
  const { play } = useSoundEffect();

  useEffect(() => {
    const id = setInterval(() => setBlink(b => !b), 620);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!gameStartedAt) { setElapsed('00:00:00'); return; }
    const update = () => {
      const secs = Math.floor((Date.now() - gameStartedAt) / 1000);
      setElapsed(`${String(Math.floor(secs/3600)).padStart(2,'0')}:${String(Math.floor((secs%3600)/60)).padStart(2,'0')}:${String(secs%60).padStart(2,'0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [gameStartedAt]);

  const handleConfirmStart = async () => {
    setStarting(true);
    play('start');
    await onStart();
    setConfirm(false); setStarting(false);
  };

  if (gameStartedAt) {
    return (
      <div className="mx-3 mt-3 rounded-xl border border-[#7ec8a0]/20 overflow-hidden" style={{ background: 'linear-gradient(135deg,#04130a,#030d07)' }}>
        <div className="px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0" style={{ background: '#7ec8a012', border: '1px solid #7ec8a025' }}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7ec8a0]" />
            </span>
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <p className="text-[#7ec8a0] text-[9px] font-bold tracking-[0.4em] uppercase shrink-0" style={{ fontFamily: FONT_DECO }}>LIVE</p>
            <p className="text-white font-black tabular-nums text-sm flex-1" style={{ fontFamily: FONT_HUD }}>{elapsed}</p>
          </div>
          <p className="text-[#1e2035] text-[10px] tracking-wide shrink-0" style={{ fontFamily: FONT_DECO }}>ELAPSED</p>
        </div>
      </div>
    );
  }

  if (confirm) {
    return (
      <div className="mx-3 mt-3 rounded-xl border border-[#d4a843]/30 p-3" style={{ background: 'linear-gradient(135deg,#120800,#0a0a00)' }}>
        <p className="text-white text-xs font-bold mb-0.5 text-center" style={{ fontFamily: FONT_BODY }}>確認開始遊戲？</p>
        <p className="text-[#3a4a2a] text-[10px] text-center mb-3 leading-relaxed" style={{ fontFamily: FONT_BODY }}>全員觸發開場特效，計時器啟動</p>
        <div className="flex gap-1.5">
          <button onClick={() => { setConfirm(false); play('click'); }} className="flex-1 py-2 rounded-xl text-xs border cursor-pointer hover:text-[#888] transition-colors whitespace-nowrap" style={{ borderColor: '#1e2035', color: '#3a4a6a', fontFamily: FONT_BODY }}>取消</button>
          <button onClick={handleConfirmStart} disabled={starting} className="flex-1 py-2 rounded-xl text-xs font-bold cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 disabled:opacity-60"
            style={{ background: '#d4a84315', color: '#d4a843', fontFamily: FONT_BODY }}>
            {starting ? <><i className="ri-loader-4-line animate-spin" />啟動中…</> : <><i className="ri-play-fill" />確認開始</>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setConfirm(true)}
        className="gs-btn mx-3 mt-3 w-[calc(100%-1.5rem)] rounded-xl overflow-hidden cursor-pointer active:scale-[0.95] transition-transform relative"
        style={{ background: 'linear-gradient(160deg,#1e1200,#130900,#0e0700)' }}
      >
        {/* Animated border glow ring */}
        <div className="gs-ring absolute inset-0 rounded-xl pointer-events-none" />

        {/* Sweep light */}
        <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
          <div className="gs-sweep absolute top-0 bottom-0 w-1/3"
            style={{ background: 'linear-gradient(90deg,transparent,rgba(212,168,67,0.12),transparent)' }} />
        </div>

        {/* Corner brackets — smaller */}
        <div className="absolute top-2 left-2 w-3 h-3 pointer-events-none" style={{ borderTop: '1.5px solid rgba(212,168,67,0.8)', borderLeft: '1.5px solid rgba(212,168,67,0.8)' }} />
        <div className="absolute top-2 right-2 w-3 h-3 pointer-events-none" style={{ borderTop: '1.5px solid rgba(212,168,67,0.8)', borderRight: '1.5px solid rgba(212,168,67,0.8)' }} />
        <div className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none" style={{ borderBottom: '1.5px solid rgba(212,168,67,0.8)', borderLeft: '1.5px solid rgba(212,168,67,0.8)' }} />
        <div className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none" style={{ borderBottom: '1.5px solid rgba(212,168,67,0.8)', borderRight: '1.5px solid rgba(212,168,67,0.8)' }} />

        {/* Corner spark dots */}
        {[
          'top-[8px] left-[8px]',
          'top-[8px] right-[8px]',
          'bottom-[8px] left-[8px]',
          'bottom-[8px] right-[8px]',
        ].map((pos, i) => (
          <div key={i} className={`gs-spark absolute ${pos} w-[5px] h-[5px] rounded-full pointer-events-none`}
            style={{ background: '#d4a843', animationDelay: `${i * 0.25}s` }} />
        ))}

        {/* Content — compact horizontal layout */}
        <div className="relative z-10 px-3 py-2.5 flex items-center gap-3">
          {/* Swords + orb */}
          <div className="flex items-center gap-2 shrink-0">
            <i className="gs-sword-l ri-sword-fill text-sm" style={{ color: '#d4a843' }} />
            <div className="gs-orb relative w-8 h-8 flex items-center justify-center rounded-full shrink-0"
              style={{ background: 'radial-gradient(circle at 40% 35%,#ffe080,#d4a843,#a06010)' }}>
              <i className="ri-play-fill text-[#1a0a00] text-sm relative z-10" style={{ marginLeft: '2px' }} />
              <div className="absolute inset-0 rounded-full gs-orb-ping opacity-40"
                style={{ background: 'rgba(212,168,67,0.5)' }} />
            </div>
            <i className="gs-sword-r ri-sword-fill text-sm" style={{ color: '#d4a843', transform: 'scaleX(-1)' }} />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p
              className="gs-title font-black tracking-[0.25em] leading-tight"
              style={{
                fontFamily: FONT_DECO,
                fontSize: '13px',
                color: '#f5c84a',
              }}
            >
              比賽正式開始
            </p>
            <p
              className="tracking-[0.4em] uppercase font-black transition-opacity duration-100 mt-0.5"
              style={{
                fontFamily: FONT_HUD,
                fontSize: '7px',
                color: 'rgba(212,168,67,0.8)',
                opacity: blink ? 1 : 0,
              }}
            >
              ◆ TAP TO START ◆
            </p>
          </div>

          {/* Right arrow */}
          <i className="ri-arrow-right-s-line text-base shrink-0" style={{ color: 'rgba(212,168,67,0.5)' }} />
        </div>
      </button>

      <style>{`
        .gs-btn {
          border: 1.5px solid rgba(212,168,67,0.5);
          animation: gsBtnPulse 2.2s ease-in-out infinite;
        }
        @keyframes gsBtnPulse {
          0%, 100% {
            box-shadow: 0 0 12px rgba(212,168,67,0.15), inset 0 0 12px rgba(212,168,67,0.02);
            border-color: rgba(212,168,67,0.4);
          }
          50% {
            box-shadow: 0 0 30px rgba(212,168,67,0.5), 0 0 60px rgba(212,168,67,0.15), inset 0 0 25px rgba(212,168,67,0.06);
            border-color: rgba(212,168,67,0.9);
          }
        }
        .gs-ring {
          border: 1px solid transparent;
          animation: gsRingRotate 3s linear infinite;
          background: transparent;
          pointer-events: none;
        }
        @keyframes gsRingRotate {
          0%   { box-shadow: 2px 0 0 0 rgba(212,168,67,0.6), 0 0 0 0 transparent; }
          25%  { box-shadow: 0 2px 0 0 rgba(212,168,67,0.6), 0 0 0 0 transparent; }
          50%  { box-shadow: -2px 0 0 0 rgba(212,168,67,0.6), 0 0 0 0 transparent; }
          75%  { box-shadow: 0 -2px 0 0 rgba(212,168,67,0.6), 0 0 0 0 transparent; }
          100% { box-shadow: 2px 0 0 0 rgba(212,168,67,0.6), 0 0 0 0 transparent; }
        }
        .gs-sweep {
          animation: gsSweep 2.5s ease-in-out infinite;
          left: -100%;
        }
        @keyframes gsSweep {
          0%   { left: -100%; opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { left: 200%; opacity: 0; }
        }
        .gs-orb {
          animation: gsOrbPulse 1.6s ease-in-out infinite;
        }
        @keyframes gsOrbPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(212,168,67,0.5); transform: scale(1); }
          50%      { box-shadow: 0 0 22px rgba(212,168,67,1), 0 0 44px rgba(212,168,67,0.35); transform: scale(1.1); }
        }
        .gs-orb-ping {
          animation: gsOrbPing 1.6s ease-out infinite;
        }
        @keyframes gsOrbPing {
          0%   { transform: scale(1); opacity: 0.4; }
          80%  { transform: scale(1.9); opacity: 0; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        .gs-title {
          animation: gsTitleGlow 1.5s ease-in-out infinite;
        }
        @keyframes gsTitleGlow {
          0%, 100% { text-shadow: 0 0 8px rgba(212,168,67,0.5); color: #e8b840; }
          50%      { text-shadow: 0 0 18px rgba(212,168,67,1), 0 0 35px rgba(212,168,67,0.5); color: #ffe060; }
        }
        .gs-sword-l {
          animation: gsSwordL 2.2s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes gsSwordL {
          0%, 100% { opacity: 0.25; transform: rotate(-35deg) scale(0.9); }
          50%      { opacity: 0.8;  transform: rotate(-50deg) scale(1.1) translateX(-2px); filter: drop-shadow(0 0 4px rgba(212,168,67,0.8)); }
        }
        .gs-sword-r {
          animation: gsSwordR 2.2s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes gsSwordR {
          0%, 100% { opacity: 0.25; transform: scaleX(-1) rotate(-35deg) scale(0.9); }
          50%      { opacity: 0.8;  transform: scaleX(-1) rotate(-50deg) scale(1.1) translateX(-2px); filter: drop-shadow(0 0 4px rgba(212,168,67,0.8)); }
        }
        .gs-spark {
          animation: gsSpark 1.4s ease-in-out infinite;
        }
        @keyframes gsSpark {
          0%, 100% { opacity: 0.2; transform: scale(0.6); box-shadow: none; }
          50%      { opacity: 1;   transform: scale(1.4); box-shadow: 0 0 6px rgba(212,168,67,0.9); }
        }
      `}</style>
    </>
  );
}

// ── Broadcast Section ─────────────────────────────────────────────────────────
function BroadcastSection() {
  const { addComment } = useComments();
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);
  const { play } = useSoundEffect();

  const handleSend = async () => {
    if (!msg.trim() || sending) return;
    setSending(true);
    await addComment('⚡系統', msg.trim(), null, 'system');
    setMsg(''); setSending(false); setSent(true);
    play('success');
    setTimeout(() => { setSent(false); setOpen(false); }, 2000);
  };

  return (
    <div className="mx-3 mt-2">
      {!open ? (
        <button onClick={() => { setOpen(true); play('click'); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all" style={{ background: 'rgba(126,200,160,0.04)', border: '1px solid rgba(126,200,160,0.12)' }}>
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7ec8a0] opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#c06b9a' }} />
          </span>
          <span className="text-[#7ec8a0] text-[10px] font-bold tracking-[0.3em] uppercase flex-1 text-left" style={{ fontFamily: FONT_DECO }}>廣播訊息</span>
          <i className="ri-megaphone-line text-[#7ec8a0]/50 text-xs" />
        </button>
      ) : (
        <div className="rounded-xl border border-[#7ec8a0]/15 p-2.5" style={{ background: 'rgba(126,200,160,0.03)' }}>
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-[#7ec8a0] text-[10px] font-bold tracking-[0.3em] uppercase flex-1" style={{ fontFamily: FONT_DECO }}>廣播訊息</span>
            {sent && <span className="text-[#7ec8a0] text-[10px] flex items-center gap-0.5" style={{ fontFamily: FONT_BODY }}><i className="ri-check-line" />已送出</span>}
            <button onClick={() => { setOpen(false); play('click'); }} className="text-[#2a3a5a] hover:text-[#666] cursor-pointer transition-colors"><i className="ri-close-line text-sm" /></button>
          </div>
          <div className="flex gap-1.5">
            <input type="text" value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key==='Enter' && handleSend()} placeholder="輸入廣播訊息…" maxLength={80}
              className="flex-1 rounded-lg px-3 py-2 text-white text-xs outline-none min-w-0" style={{ background: '#0a0b16', border: '1px solid #16172a', fontFamily: FONT_BODY }} />
            <button onClick={handleSend} disabled={!msg.trim()||sending} className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-all disabled:opacity-30 shrink-0" style={{ background: '#7ec8a015', border: '1px solid #7ec8a025', color: '#7ec8a0' }}>
              {sending ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-send-plane-fill text-sm" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Score History ─────────────────────────────────────────────────────────────
function ScoreHistory({ scores }: { scores: ReturnType<typeof useScore>['scores'] }) {
  if (scores.history.length === 0) return null;
  return (
    <div>
      <p className="text-[#2a3a5a] text-[10px] tracking-[0.35em] uppercase mb-2.5" style={{ fontFamily: FONT_DECO }}>最近記錄</p>
      <div className="space-y-1.5">
        {scores.history.slice(0,6).map(ev => {
          const c = CATEGORIES.find(c => c.key === ev.category);
          const teamColor = ev.team==='black' ? '#d4a843' : '#e0e0e0';
          return (
            <div key={ev.id} className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border" style={{ background: '#080916', borderColor: '#12131e' }}>
              <div className="w-1.5 h-6 rounded-full shrink-0" style={{ background: teamColor+'55' }} />
              <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0" style={{ background: `${c?.color??'#888'}12` }}>
                <i className={`${c?.icon??'ri-star-line'} text-[10px]`} style={{ color: c?.color??'#888' }} />
              </div>
              {ev.memberName && <span className="text-[10px] px-1.5 py-0.5 rounded-md shrink-0" style={{ background: '#f0a86e10', color: '#f0a86e', fontFamily: FONT_BODY }}>{ev.memberName}</span>}
              <span className="text-[#3a4a6a] text-xs truncate flex-1" style={{ fontFamily: FONT_BODY }}>{ev.note || c?.label}</span>
              <span className="text-xs font-black shrink-0" style={{ color: ev.points>=0 ? '#7ec8a0' : '#e05555', fontFamily: FONT_HUD }}>{ev.points>=0 ? '+' : ''}{ev.points}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function ScoreControlPanel() {
  const { showJudgePanel, setShowJudgePanel, resetScores, scores, gameStartedAt, startGame } = useScore();
  const [unlocked, setUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState<'score'|'task'|'member'|'lottery'|'rating'|'angel'|'reset'>('score');
  const [scoreMode, setScoreMode] = useState<'team'|'individual'>('team');
  const [showConfirm, setShowConfirm] = useState(false);
  const [feedback, setFeedback] = useState<string|null>(null);
  const { play } = useSoundEffect();

  useEffect(() => { if (!showJudgePanel) { setUnlocked(false); setActiveTab('score'); } }, [showJudgePanel]);
  useEffect(() => { document.body.style.overflow = showJudgePanel ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [showJudgePanel]);

  const handleFeedback = (msg: string) => { setFeedback(msg); setTimeout(() => setFeedback(null), 2500); };
  const handleReset = () => { resetScores(); setShowConfirm(false); handleFeedback('✓ 積分已重置'); play('warning'); };

  if (!showJudgePanel) return null;

  const TABS = [
    { key:'score',   icon:'ri-scales-3-line',   label:'積分', color:'#d4a843' },
    { key:'task',    icon:'ri-lock-unlock-line', label:'任務', color:'#7ec8a0' },
    { key:'member',  icon:'ri-group-line',       label:'人員', color:'#a78bfa' },
    { key:'lottery', icon:'ri-lottery-line',     label:'抽籤', color:'#f0a86e' },
    { key:'rating',  icon:'ri-star-fill',        label:'評分', color:'#d4a843' },
    { key:'angel',   icon:'ri-spy-line',         label:'隱藏', color:'#c06b9a' },
    { key:'reset',   icon:'ri-restart-line',     label:'重置', color:'#e05555' },
  ] as const;

  const closePanel = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setShowJudgePanel(false); play('click'); };

  return (
    <>
      <div className="fixed inset-0 z-40 cursor-pointer" style={{ background: 'rgba(3,3,12,0.75)', backdropFilter: 'blur(4px)' }} onClick={() => { setShowJudgePanel(false); play('click'); }} />
      <div
        className="fixed right-0 bottom-0 z-50 flex flex-col overflow-hidden w-full sm:w-[360px] sm:top-0 h-[90dvh] sm:h-full rounded-t-2xl sm:rounded-none"
        style={{ background: 'linear-gradient(160deg,#0b0c1a 0%,#06060f 100%)', borderLeft: '1.5px solid #16172a', boxShadow: '-16px 0 60px rgba(0,0,0,0.8)' }}
      >
        {showConfirm && <ConfirmDialog onConfirm={handleReset} onCancel={() => { setShowConfirm(false); play('click'); }} />}
        <div className="absolute top-0 left-0 right-0 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg,transparent,#d4a84360,transparent)' }} />

        {/* Header */}
        <div className="px-4 py-3 border-b border-[#16172a] flex items-center gap-3 shrink-0" style={{ background: 'linear-gradient(90deg,#d4a84308,transparent 60%)' }}>
          <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ background: '#d4a84312', border: '1px solid rgba(212,168,67,0.3)' }}>
            <i className="ri-scales-3-line text-[#d4a843] text-base" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-white text-sm font-black tracking-wider" style={{ fontFamily: FONT_BODY }}>系統管理</h2>
            <p className="text-[#2a4a6a] text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: FONT_DECO }}>{unlocked ? 'JUDGE CONTROL' : 'ACCESS REQUIRED'}</p>
          </div>
          {unlocked && gameStartedAt && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border shrink-0" style={{ background: '#e0555510', borderColor: '#e0555330' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#e05555] animate-pulse inline-block" />
              <span className="text-[#e05555] text-[10px] font-black tracking-widest" style={{ fontFamily: FONT_DECO }}>LIVE</span>
            </div>
          )}
          <button
            onClick={closePanel}
            className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all active:scale-90 shrink-0"
            style={{ background: '#16172a', border: '1px solid #1e2035', color: '#555' }}
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {!unlocked ? (
          <>
            <div className="flex-1 overflow-y-auto"><PinLock onUnlock={() => { setUnlocked(true); }} /></div>
          </>
        ) : (
          <>
            <div className="shrink-0">
              <GameStartBanner gameStartedAt={gameStartedAt} onStart={startGame} />
              <BroadcastSection />
            </div>

            {/* Tab bar */}
            <div className="px-3 pt-2 pb-0 shrink-0">
              <div className="flex rounded-xl p-1 gap-0.5" style={{ background: '#080916', border: '1px solid #12131e' }}>
                {TABS.map(t => {
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => { setActiveTab(t.key as typeof activeTab); play('click'); }}
                      className="flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg cursor-pointer transition-all duration-200 whitespace-nowrap"
                      style={{ background: active ? `${t.color}15` : 'transparent', minHeight: '48px' }}
                    >
                      <i className={`${t.icon} text-base`} style={{ color: active ? t.color : '#2a3a5a' }} />
                      <span className="text-[9px] font-black tracking-wide" style={{ color: active ? t.color : '#2a3a5a', fontFamily: FONT_BODY }}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div key={activeTab} className="flex-1 overflow-y-auto overscroll-contain">
              {activeTab === 'score' ? (
                <div className="px-3 py-3 space-y-3">
                  {/* Mini scoreboard */}
                  <div className="relative rounded-xl overflow-hidden border border-[#16172a]" style={{ background: 'linear-gradient(160deg,#0c0d1a,#080910)' }}>
                    <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg,transparent,#d4a84340,transparent)' }} />
                    <div className="grid grid-cols-[1fr_auto_1fr]">
                      <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5">
                        <span className="text-[#d4a843]/50 text-[9px] font-black tracking-[0.3em] uppercase" style={{ fontFamily: FONT_DECO }}>黑隊</span>
                        <div className="text-[#d4a843] font-black tabular-nums leading-none" style={{ fontSize: '2rem', fontFamily: FONT_HUD, textShadow: '0 0 14px rgba(212,168,67,0.4)' }}>
                          {scores.black.battle+scores.black.chemistry+scores.black.dreamCoins}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {(['battle','chemistry'] as ScoreCategory[]).map(key => (
                            <span key={key} className="text-[9px] px-1.5 py-px rounded-full tabular-nums font-bold" style={{ background: key==='battle'?'#e0555512':'#d4a84312', color: key==='battle'?'#e05555':'#d4a843', fontFamily: FONT_HUD }}>{scores.black[key]}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-center px-2">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-px h-5" style={{ background: 'linear-gradient(180deg,transparent,#d4a84330,transparent)' }} />
                          <span className="text-[#1e2035] text-[9px] font-black tracking-widest" style={{ fontFamily: FONT_DECO }}>VS</span>
                          <div className="w-px h-5" style={{ background: 'linear-gradient(180deg,transparent,#d4a84330,transparent)' }} />
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5">
                        <span className="text-white/25 text-[9px] font-black tracking-[0.3em] uppercase" style={{ fontFamily: FONT_DECO }}>白隊</span>
                        <div className="text-white font-black tabular-nums leading-none" style={{ fontSize: '2rem', fontFamily: FONT_HUD, textShadow: '0 0 14px rgba(255,255,255,0.12)' }}>
                          {scores.white.battle+scores.white.chemistry+scores.white.dreamCoins}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {(['battle','chemistry'] as ScoreCategory[]).map(key => (
                            <span key={key} className="text-[9px] px-1.5 py-px rounded-full tabular-nums font-bold" style={{ background: key==='battle'?'#e0555512':'#d4a84312', color: key==='battle'?'#e05555':'#d4a843', fontFamily: FONT_HUD }}>{scores.white[key]}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Mode toggle */}
                  <div className="flex p-1 rounded-xl gap-0.5" style={{ background: '#080916', border: '1px solid #12131e' }}>
                    <button onClick={() => { setScoreMode('team'); play('click'); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
                      style={scoreMode==='team' ? { background: '#d4a84318', color: '#d4a843', border: '1px solid #d4a84330' } : { color: '#2a3a4a', border: '1px solid transparent' }}>
                      <i className="ri-shield-line text-sm" /><span style={{ fontFamily: FONT_BODY }}>隊伍給分</span>
                    </button>
                    <button onClick={() => { setScoreMode('individual'); play('click'); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
                      style={scoreMode==='individual' ? { background: '#f0a86e18', color: '#f0a86e', border: '1px solid #f0a86e30' } : { color: '#2a3a4a', border: '1px solid transparent' }}>
                      <i className="ri-user-star-line text-sm" /><span style={{ fontFamily: FONT_BODY }}>個人給分</span>
                    </button>
                  </div>
                  {scoreMode==='team' ? <TeamScoreTab onFeedback={handleFeedback} /> : <IndividualScoreTab onFeedback={handleFeedback} />}
                  {feedback && (
                    <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#7ec8a0]/20" style={{ background: 'rgba(126,200,160,0.05)' }}>
                      <i className="ri-check-circle-fill text-[#7ec8a0] text-sm" />
                      <span className="text-[#7ec8a0] text-xs font-bold" style={{ fontFamily: FONT_BODY }}>{feedback}</span>
                    </div>
                  )}
                  <ScoreHistory scores={scores} />
                  <SettlementButton onFeedback={handleFeedback} />
                  <div className="pt-2 border-t border-[#12131e]">
                    <button
                      onClick={() => { setShowConfirm(true); play('warning'); }}
                      className="flex items-center gap-2 text-xs cursor-pointer py-1.5 transition-colors"
                      style={{ color: 'rgba(224,85,85,0.35)', fontFamily: FONT_BODY }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color='rgba(224,85,85,0.75)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color='rgba(224,85,85,0.35)'; }}
                    >
                      <i className="ri-restart-line" />僅重置積分
                    </button>
                  </div>
                </div>
              ) : activeTab==='task' ? <TaskManagementPanel />
                : activeTab==='member' ? <MemberManagementPanel />
                : activeTab==='lottery' ? <LotteryManagementPanel />
                : activeTab==='rating' ? <RatingPanel />
                : activeTab==='angel' ? <SecretAngelPanel />
                : <ResetTab onFeedback={handleFeedback} />
              }
              {activeTab !== 'score' && feedback && (
                <div className="px-3 pb-3">
                  <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#7ec8a0]/20" style={{ background: 'rgba(126,200,160,0.05)' }}>
                    <i className="ri-check-circle-fill text-[#7ec8a0] text-sm" />
                    <span className="text-[#7ec8a0] text-xs font-bold" style={{ fontFamily: FONT_BODY }}>{feedback}</span>
                  </div>
                </div>
              )}
              <div style={{ height: 'env(safe-area-inset-bottom, 16px)', minHeight: '16px' }} />
            </div>

            {/* Bottom bar */}
            <div className="shrink-0 px-3 py-3 border-t" style={{ borderColor: '#16172a', background: 'linear-gradient(180deg,#06060f,#0b0c1a)' }}>
              <button onClick={closePanel} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl cursor-pointer active:scale-[0.97] transition-transform whitespace-nowrap" style={{ background: '#7ec8a012', border: '1px solid #7ec8a040', color: '#7ec8a0', fontFamily: FONT_BODY }}>
                <i className="ri-arrow-left-line text-sm" /><span className="text-sm font-bold">返回遊戲</span>
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 15%{transform:translateX(-8px)} 30%{transform:translateX(8px)} 45%{transform:translateX(-6px)} 60%{transform:translateX(6px)} 75%{transform:translateX(-4px)} 90%{transform:translateX(4px)} }
        @keyframes shimBtn { from{transform:translateX(-200%)} to{transform:translateX(200%)} }
        @keyframes pinRing { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.12); opacity: 0.15; } }
      `}</style>
    </>
  );
}
